/**
 * Utilitários de calendário compartilhados pela Agenda.
 *
 * Tudo aqui trabalha com `Date` no fuso do NAVEGADOR. O servidor guarda
 * instantes UTC e a equipe comercial de um workspace opera num fuso só, então
 * exibir no relógio de quem está olhando é o comportamento certo e o mais
 * simples de conferir. Se um dia a agenda precisar ser vista de outro país, o
 * ponto de mudança é aqui e no `timezone` gravado na reunião.
 */

import {
  FUSO_PADRAO,
  horaNoFuso,
  mesmaHoraDeParede,
  rotuloDoFuso,
} from "@shared/timezone";

export const DIAS_CURTOS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
export const DIAS_LONGOS = [
  "Domingo",
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
];

/** Domingo da semana que contém `d`, à meia-noite local. */
export function inicioDaSemana(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  out.setDate(out.getDate() - out.getDay());
  return out;
}

export function somaDias(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

/** Os 7 dias da semana de `d`, começando no domingo. */
/**
 * As semanas inteiras (dom–sáb) que cobrem o mês de `d` — 5 ou 6 linhas,
 * como o Google mostra. Dias do mês vizinho entram para completar a linha.
 */
export function diasDoMes(d: Date): Date[] {
  const primeiro = new Date(d.getFullYear(), d.getMonth(), 1);
  const ultimo = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const inicio = inicioDaSemana(primeiro);
  const out: Date[] = [];
  for (let x = inicio; x <= ultimo || out.length % 7 !== 0; x = somaDias(x, 1)) out.push(x);
  return out;
}

export function diasDaSemana(d: Date): Date[] {
  const inicio = inicioDaSemana(d);
  return Array.from({ length: 7 }, (_, i) => somaDias(inicio, i));
}

export function mesmoDia(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function ehHoje(d: Date): boolean {
  return mesmoDia(d, new Date());
}

/** "YYYY-MM-DD" no fuso local — o formato que `freeSlots` espera. */
export function isoLocalDate(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** "HH:mm" no fuso local. */
export function hhmm(d: Date | string): string {
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

/**
 * Valor para `<input type="datetime-local">` — precisa ser hora LOCAL sem
 * fuso. `toISOString()` aqui seria o erro clássico: mostraria a reunião das
 * 14h como 17h.
 */
export function paraInputLocal(d: Date | string): string {
  const date = typeof d === "string" ? new Date(d) : d;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}T${p(date.getHours())}:${p(date.getMinutes())}`;
}

/** Inverso do anterior: o texto do input é lido como hora local. */
export function doInputLocal(v: string): Date {
  return new Date(v);
}

export function duracaoEmMinutos(inicio: Date | string, fim: Date | string): number {
  const a = typeof inicio === "string" ? new Date(inicio) : inicio;
  const b = typeof fim === "string" ? new Date(fim) : fim;
  return Math.round((b.getTime() - a.getTime()) / 60_000);
}

export function rotuloDeDuracao(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, "0")}`;
}

export const STATUS_REUNIAO: Record<
  string,
  { label: string; tint: string; barra: string }
> = {
  scheduled: {
    label: "Marcada",
    tint: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300",
    barra: "border-l-sky-500",
  },
  confirmed: {
    label: "Confirmada",
    tint: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
    barra: "border-l-emerald-500",
  },
  done: {
    label: "Realizada",
    tint: "bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-300",
    barra: "border-l-slate-400",
  },
  canceled: {
    label: "Cancelada",
    tint: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300",
    barra: "border-l-red-500",
  },
  no_show: {
    label: "Não compareceu",
    tint: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
    barra: "border-l-amber-500",
  },
};

export const TIPO_LOCAL: Record<string, string> = {
  online: "Online",
  presencial: "Presencial",
  telefone: "Telefone",
};

/**
 * Como mostrar o horário de uma reunião marcada em OUTRO fuso.
 *
 * Devolve a hora no fuso da reunião e, só quando o relógio de quem está
 * olhando marca outra coisa, a hora local também. Mostrar sempre as duas
 * polui a agenda de quem nunca sai do próprio fuso — que é a maioria.
 */
/**
 * Nome curto do fuso — "Brasília", "Cuiabá", "Manaus". O rótulo completo
 * ("Brasília (SP, RJ, MG, Sul, Nordeste)") serve para escolher numa lista,
 * não para ler ao lado do horário.
 */
export function fusoCurto(tz: string): string {
  const completo = rotuloDoFuso(tz);
  return completo.split("(")[0].trim();
}

export function horarioComFuso(
  inicio: Date | string,
  fim: Date | string,
  fusoDaReuniao: string,
): { principal: string; local: string | null; rotulo: string } {
  const a = typeof inicio === "string" ? new Date(inicio) : inicio;
  const b = typeof fim === "string" ? new Date(fim) : fim;
  const meu = Intl.DateTimeFormat().resolvedOptions().timeZone || FUSO_PADRAO;
  const principal = `${horaNoFuso(a, fusoDaReuniao)} – ${horaNoFuso(b, fusoDaReuniao)}`;
  const difere = !mesmaHoraDeParede(a, fusoDaReuniao, meu);
  return {
    principal,
    local: difere ? `${horaNoFuso(a, meu)} – ${horaNoFuso(b, meu)}` : null,
    rotulo: fusoCurto(fusoDaReuniao),
  };
}

// ─── Avatares de pessoa ──────────────────────────────────────────────────────
// Ficam aqui porque o formulário e a visualização da reunião precisam pintar
// a MESMA pessoa com a mesma cor — duas tabelas separadas divergiriam.

const TONS_AVATAR = [
  "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
  "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
];

/** Cor estável por pessoa: o mesmo id sempre com o mesmo tom. */
export function tomDoAvatar(id: number): string {
  return TONS_AVATAR[id % TONS_AVATAR.length];
}

export function iniciais(nome?: string | null, email?: string | null): string {
  const base = (nome ?? email ?? "?").trim();
  const partes = base.split(/\s+/).filter(Boolean);
  if (partes.length >= 2) return (partes[0][0] + partes[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

export function primeiroNome(nome?: string | null, email?: string | null): string {
  return (nome ?? email ?? "Participante").split(" ")[0];
}
